import { SafetyControlSurveyNote } from "src/api/generated/erp/db/types/tables/safetyControlSurveyNote.ts";
import { SafetyControlSurveyNoteView } from "src/api/generated/erp/db/types/tables/safetyControlSurveyNoteView.ts";
import {
	SafetyControlSurveyNoteApi,
	SafetyControlSurveyNoteApi_NoteFormInitData,
} from "src/api/generated/erp/safetyControlSurvey/api/safetyControlSurveyNoteApi.ts";
import { AsyncForm } from "src/components/common/forms/AsyncForm.tsx";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import { FormMultiFileInputField } from "src/components/common/forms/fields/FormMultiFileInputField.tsx";
import i18n from "i18next";
import { requireRule } from "src/components/common/forms/validation.ts";
import { uploadFiles } from "src/utils/fileUploading.ts";
import { FormCommonProps } from "src/components/common/forms/types.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SafetyControlSurveyNoteFormProps {
	note: SafetyControlSurveyNoteView | undefined;
	safetyControlSurveyId: number;
	onCompleted: FormCommonProps<number>["onCompleted"];
	onFormEdited: () => void;
}

export interface FormValues extends SafetyControlSurveyNote {
	files: File[];
}

export const SafetyControlSurveyNoteForm = ({
	note,
	safetyControlSurveyId,
	onCompleted,
	onFormEdited,
}: SafetyControlSurveyNoteFormProps) => {
	const isNew = note == null;

	return (
		<AsyncForm<SafetyControlSurveyNoteApi_NoteFormInitData, FormValues, number>
			fetch={() => SafetyControlSurveyNoteApi.getNoteFormInitData()}
			getDefaultValues={() =>
				note != null ?
					{
						...note,
						files: [],
					}
				:	{
						safetyControlSurveyId: safetyControlSurveyId,
						files: [],
					}
			}
			submit={async ({ files, ...note }) => {
				const fileHandles = files.length > 0 ? await uploadFiles(files) : [];

				if (isNew) {
					const result = await SafetyControlSurveyNoteApi.insert({
						note: note,
						files: fileHandles,
					});
					return result.safetyControlSurveyNoteId;
				} else {
					const result = await SafetyControlSurveyNoteApi.update({
						note: note,
						files: fileHandles,
					});
					return result.safetyControlSurveyNoteId;
				}
			}}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			columns={1}
			agentContext={{
				key: defineAgentUiContextKey("erp.safetyControl.surveyNote.form"),
				data: {
					safetyControlSurveyId,
					safetyControlSurveyNoteId: note?.safetyControlSurveyNoteId ?? null,
				},
			}}
			render={({ control, data: initData }) => (
				<>
					<FormSelectField
						control={control}
						name="categoryId"
						label={i18n.t("category")}
						options={initData.categoryOptions}
						getOptionKey={(o) => o.safetyControlSurveyNoteCategoryId}
						getOptionLabel={(o) => o.name}
					/>
					<FormTextField
						control={control}
						name="note"
						label={i18n.t("note")}
						multiline
						minRows={3}
						rules={requireRule()}
					/>
					<FormMultiFileInputField control={control} name="files" label={i18n.t("attachments")} />
				</>
			)}
		/>
	);
};
